import React, {
  forwardRef, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import Avatar from './Avatar';

export type MentionUser = {
  id: string;
  name?: string;
  email?: string;
  designation?: string;
};

export type MentionsAutocompleteHandle = {
  moveUp(): void;
  moveDown(): void;
  pickCurrent(): void;
  hasSelection(): boolean;
};

type Props = {
  visible: boolean;
  query: string;
  users: MentionUser[];
  onSelect: (u: MentionUser) => void;
};

const MAX_RESULTS = 8;

const MentionsAutocomplete = forwardRef<MentionsAutocompleteHandle, Props>(
  ({ visible, query, users, onSelect }, ref) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const filtered = useMemo<MentionUser[]>(() => {
      if (!visible) return [];
      const q = (query || '').trim().toLowerCase();
      const list = (users || []).filter(u => {
        if (!q) return true;
        const n = (u.name || '').toLowerCase();
        const e = (u.email || '').toLowerCase();
        return n.includes(q) || e.includes(q);
      });
      return list.slice(0, MAX_RESULTS);
    }, [visible, query, users]);

    // Reset active index on query / visibility change
    useEffect(() => {
      setActiveIndex(0);
    }, [query, visible]);

    // Keep activeIndex in bounds
    useEffect(() => {
      if (activeIndex >= filtered.length) {
        setActiveIndex(filtered.length > 0 ? filtered.length - 1 : 0);
      }
    }, [filtered.length, activeIndex]);

    useImperativeHandle(ref, () => ({
      moveUp: () => {
        setActiveIndex(i => (filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length));
      },
      moveDown: () => {
        setActiveIndex(i => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
      },
      pickCurrent: () => {
        const u = filtered[activeIndex];
        if (u) onSelect(u);
      },
      hasSelection: () => filtered.length > 0,
    }), [filtered, activeIndex, onSelect]);

    if (!visible) return null;

    return (
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-xl max-h-[240px] overflow-y-auto"
        onMouseDown={e => e.preventDefault()} // prevent textarea blur
      >
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-gray-400 text-center">No users match</div>
        ) : (
          filtered.map((u, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                type="button"
                key={u.id}
                onClick={() => onSelect(u)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 transition-colors ${
                  isActive ? 'bg-blue-50 ring-1 ring-blue-400 ring-inset' : 'hover:bg-gray-50'
                }`}
              >
                <Avatar name={u.name} size={6} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-800 truncate">
                    {u.name || 'Unknown'}
                  </div>
                  {(u.designation || u.email) && (
                    <div className="text-[11px] text-gray-500 truncate">
                      {u.designation || u.email}
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    );
  }
);

MentionsAutocomplete.displayName = 'MentionsAutocomplete';

export default MentionsAutocomplete;
